/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import useEinstein from '../../../hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '../../../hooks/use-current-basket'
import {useSplioService} from './../../../hooks/use-splio-service'
import {useHistory, useLocation} from 'react-router-dom'
import {useToast} from '../../../hooks/use-toast'

// Custom Hooks
import useShipToCountry from '../../../hooks/use-ship-to-country'
import {useGTM} from '@app/hooks/use-gtm'

// Utils
import {getDefaultShipment, isValidAddress} from '../../../utils/basket-utils'

const CheckoutContext = React.createContext()

/**
 * Provides the checkout context and manages the state of the checkout process.
 *
 * This component is responsible for determining the current step in the checkout process
 * based on the customer's status and the contents of the basket. It also handles specific
 * errors related to tax and payment failures, and communicates with external services
 * such as Einstein and Splio.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 *
 * @returns {JSX.Element} A context provider component that supplies checkout-related state and actions.
 *
 * @example
 * <CheckoutProvider>
 *     <YourComponent />
 * </CheckoutProvider>
 *
 * @context
 * The context value includes:
 * - `step` {number}: The current step in the checkout process.
 * - `STEPS` {Object}: An object mapping step names to their respective indices.
 * - `goToNextStep` {Function}: A function to advance to the next step in the checkout process.
 * - `goToStep` {Function}: A function to jump to a specific step in the checkout process.
 *
 * @dependencies
 * Utilizes several hooks and services:
 * - `useCurrentCustomer`: Fetches the current customer data.
 * - `useCurrentBasket`: Fetches the current basket data and provides a refetch function.
 * - `useEinstein`: Provides functions to send checkout-related events to Einstein.
 * - `useSplioService`: Provides a function to send the basket to Splio.
 * - `useToast`: Provides a function to display toast notifications.
 * - `useHistory`: Provides navigation capabilities.
 * - `useLocation`: Provides access to the current URL location.
 * - `useIntl`: Provides internationalization capabilities.
 * - `useShipToCountry`: Provides information about the shipping destination country.
 */
export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {shipToCountry} = useShipToCountry()
    const {
        data: basket,
        derivedData: {isBuyingOnlyGiftCards}
    } = useCurrentBasket()
    const einstein = useEinstein()
    const [step, setStep] = useState()
    const [activeCCStore, setActiveCCStore] = useState()
    const [isClearShippingForm, setIsClearShippingForm] = useState(false)
    const {sendBasketToSplio} = useSplioService()
    const toast = useToast()
    const history = useHistory()
    const location = useLocation()
    const {formatMessage} = useIntl()
    const params = new URLSearchParams(location.search)
    const [products, setProducts] = useState([])
    const [shippingMethodSubmitted, setShippingMethodSubmitted] = useState(false)

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'SHIPPING_OPTIONS',
        'PAYMENT',
        'REVIEW_ORDER'
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})

    const getCheckoutStepName = (step) => CHECKOUT_STEPS_LIST[step]
    const {usePageLoadAndCookies, triggerCheckoutEvents} = useGTM()
    const DEFAULT_SHIPMENT = getDefaultShipment(basket)

    /**
     * Determines the current step in the checkout process based on the state of the customer and basket.
     * It also handles specific error scenarios related to Avatax and payment failures.
     *
     * @function
     * @param {Object} customer - The customer object containing information about the current user.
     * @param {boolean} customer.isGuest - Indicates if the current user is a guest.
     * @param {Object} basket - The basket object representing the current shopping cart.
     * @param {Array} basket.shipments - Array of shipment objects associated with the basket.
     * @param {Object} basket.customerInfo - Information about the customer associated with the basket.
     * @param {string} basket.customerInfo.email - The email address of the customer.
     * @param {Array} basket.paymentInstruments - Payment instruments associated with the basket.
     * @param {Object} basket.billingAddress - The billing address associated with the basket.
     * @param {Object} location - The location object representing the current URL.
     * @param {string} location.pathname - The path of the current URL.
     * @param {URLSearchParams} params - URL search parameters for the current location.
     * @param {Object} history - The history object used to manipulate the browser's session history.
     * @param {Function} toast - Function to display toast notifications.
     * @param {Function} formatMessage - Function to format messages for display.
     * @param {Function} sendBasketToSplio - Function to send the basket data to Splio.
     * @param {Function} setStep - Function to update the current step in the checkout process.
     *
     * @returns {void}
     */
    useEffect(() => {
        if (!customer || !basket) {
            return
        }

        let step = STEPS.PAYMENT

        if (customer.isGuest && !basket.customerInfo?.email) {
            step = STEPS.CONTACT_INFO
        } else if (isBuyingOnlyGiftCards) {
            // When buying only gift cards, skip the shipping settings
            step = STEPS.PAYMENT
        } else if (
            !isValidAddress(DEFAULT_SHIPMENT?.shippingAddress) ||
            DEFAULT_SHIPMENT?.shippingAddress?.countryCode !== shipToCountry?.id ||
            (basket?.c_geographicalCountryCode &&
                DEFAULT_SHIPMENT?.shippingAddress?.countryCode !==
                    basket?.c_geographicalCountryCode) // Overseas territory
        ) {
            step = STEPS.SHIPPING_ADDRESS
        } else if (
            !DEFAULT_SHIPMENT?.shippingMethod ||
            !DEFAULT_SHIPMENT.c_isAddressAndMethodSynced
        ) {
            step = STEPS.SHIPPING_OPTIONS
        } else if (!basket.paymentInstruments || !basket.billingAddress) {
            step = STEPS.PAYMENT
        }

        // Handle Avatax error
        if (location.pathname.indexOf('checkout') > -1 && params.get(`avataxFailure`) === 'true') {
            params.delete('avataxFailure')
            history.replace({
                search: params.toString()
            })
            toast({
                title: formatMessage({
                    defaultMessage: 'Unable to compute the sales tax, please try again',
                    id: 'checkout.avatax.error'
                }),
                status: 'error'
            })
        }

        // Handle Zonos error
        if (location.pathname.indexOf('checkout') > -1 && params.get(`zonosFailure`) === 'true') {
            params.delete('zonosFailure')
            history.replace({
                search: params.toString()
            })
            toast({
                title: formatMessage({
                    defaultMessage: 'Unable to compute the sales tax, please try again',
                    id: 'checkout.avatax.error'
                }),
                status: 'error'
            })
        }

        if (location.pathname.includes('checkout') && params.get(`paymentFailure`) === 'true') {
            params.delete('paymentFailure')
            history.replace({
                search: params.toString()
            })
        }

        if (step === 1 && basket?.customerInfo?.email !== '') {
            sendBasketToSplio({
                isCompleted: false,
                basket: basket,
                requestType: 'checkout'
            })
        }

        setStep(step)
        // Reset the submission state to acknowledge processing
        if (shippingMethodSubmitted) {
            // GTM Integration
            // Send analytics add_shipping_info
            triggerCheckoutEvents(products, 'add_shipping_info')
            setShippingMethodSubmitted(false)
        }
    }, [
        customer?.isGuest,
        basket?.customerInfo?.email,
        DEFAULT_SHIPMENT?.shippingAddress,
        basket?.paymentInstruments,
        basket?.billingAddress,
        basket?.basketId,
        shippingMethodSubmitted
    ])

    /**************** Einstein ****************/
    // Run this once when checkout begins
    useEffect(() => {
        if (basket?.productItems) {
            einstein.sendBeginCheckout(basket)
        }
    }, [])

    useEffect(() => {
        einstein.sendCheckoutStep(getCheckoutStepName(step), step, basket)
    }, [step])

    usePageLoadAndCookies('checkout')

    const value = {
        step,
        STEPS,
        activeCCStore,
        products,
        setProducts,
        isClearShippingForm,
        goToNextStep: () => setStep(step + 1),
        goToStep: (step) => setStep(step),
        setActiveCCStore: (store) => setActiveCCStore(store),
        setIsClearShippingForm: (isClear) => setIsClearShippingForm(isClear),
        submitShippingMethod: () => setShippingMethodSubmitted(true)
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
